import type { User, UserInfo } from '../types';

/**
 * DRI-95: the report PDF switches (`ReportPdf:SoilEnabled`, `ReportPdf:SapTissueEnabled`) as the SPA
 * sees them on `/auth/me`. One switch per report type; a batch's type is SOIL when it has a SOIL link
 * (the server's `HasSoilLinksAsync` predicate), else SAP/Tissue.
 *
 * Absent means ON everywhere: an API image that predates the fields keeps today's PDF buttons. These
 * flags only hide UI; the server refuses a PDF on an off batch regardless (400/409), so it stays the
 * authority when the client's soil classification disagrees.
 */
export type ReportPdfFlags = Pick<User, 'reportPdfSoilEnabled' | 'reportPdfSapTissueEnabled'>;

/** Map the raw `/auth/me` fields, defaulting each absent switch to `true`. */
export function reportPdfFlagsFromUserInfo(
  info: Pick<UserInfo, 'reportPdfSoilEnabled' | 'reportPdfSapTissueEnabled'>,
): Required<ReportPdfFlags> {
  return {
    reportPdfSoilEnabled: info.reportPdfSoilEnabled ?? true,
    reportPdfSapTissueEnabled: info.reportPdfSapTissueEnabled ?? true,
  };
}

/**
 * True when the report PDF is generated for a batch of this type. `hasSoil` is the batch's soil
 * classification (staff: `sampleTypes` include SOIL; portal: `isSoil`). No user, or an absent switch,
 * reads as on.
 */
export function isReportPdfEnabled(user: ReportPdfFlags | null | undefined, hasSoil: boolean): boolean {
  const flag = hasSoil ? user?.reportPdfSoilEnabled : user?.reportPdfSapTissueEnabled;
  return flag ?? true;
}

/** Staff classifier: a batch is soil when any of its sample types is SOIL (case-insensitive). */
export function batchHasSoil(sampleTypes: readonly (string | null | undefined)[] | null | undefined): boolean {
  return (sampleTypes ?? []).some((t) => (t ?? '').trim().toUpperCase() === 'SOIL');
}
